import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { activityFromPath, type HumanTimeDevice } from '@shared/human-time';
import { postActivity } from '@/lib/activity-api';
import { ActivityTracker, type ActivityContext, type ActivitySender } from '@/lib/activity-tracker';
import { getCurrentProject } from '@/lib/current-project';
import { useTaskStore } from '@/stores/taskStore';
import { PHONE_QUERY } from './use-visual-viewport';

/** How often a tab checks whether it's active, and how often it sends what it counted. */
const SAMPLE_MS = 15_000;
const FLUSH_MS = 60_000;
/** Scrolling fires many events: count at most one a second. */
const INPUT_THROTTLE_MS = 1_000;

const INPUT_EVENTS = ['keydown', 'pointerdown', 'wheel', 'scroll', 'touchstart'] as const;

/** Taken, or refused for good (a bad body); a lost connection, a server error, a session being renewed or rate limit is retried. */
const sendActivity: ActivitySender = async (minutes, { keepalive }) => {
  const res = await postActivity(minutes, { keepalive });
  if (res.status >= 200 && res.status < 300) return true;
  return res.status >= 400 && res.status < 500 && res.status !== 401 && res.status !== 408 && res.status !== 429;
};

/** Mobile while the app shows its phone layout (under 640px wide), else desktop (SF-T-281). */
function currentDevice(): HumanTimeDevice {
  return window.matchMedia(PHONE_QUERY).matches ? 'mobile' : 'desktop';
}

/**
 * The tab's project (when its page is in one and it has loaded), open task, area and device. A task open
 * in the floating Tasks panel (any page of a project) counts when the page itself has none open. While
 * the chat panel (admins) covers the page, or the person's last input was in it, the minute is chat's,
 * kept across projects (the chat panel works for no project's task).
 */
export function currentActivityContext(
  pathname: string,
  panelTaskId: string | null = openPanelTask(),
  inChat: boolean = false,
  device: HumanTimeDevice = currentDevice()
): ActivityContext | null {
  if (inChat) return { project_id: null, task_id: null, area: 'chat', device };
  const fromPath = activityFromPath(pathname);
  const { projectSlug } = fromPath;
  const taskId = fromPath.taskId ?? (projectSlug ? panelTaskId : null);
  const area = taskId ? 'task' : fromPath.area;
  if (!projectSlug) return { project_id: null, task_id: taskId, area, device };
  const project = getCurrentProject();
  if (!project || project.slug !== projectSlug) return null;
  return { project_id: project.id, task_id: taskId, area, device };
}

/** The chat panel's frame (pages/chat/ChatDockFrame.tsx). */
const CHAT_PANEL = '[data-chat-panel]';

/** Whether the minute is the chat panel's: it covers the page (a phone), or the last input was inside it. */
function chatHasTheTab(lastInputInChat: boolean): boolean {
  const panel = document.querySelector(CHAT_PANEL);
  if (!panel) return false;
  return panel.getAttribute('aria-modal') === 'true' || lastInputInChat;
}

function openPanelTask(): string | null {
  const { panel } = useTaskStore.getState();
  return panel.open && panel.taskId ? panel.taskId : null;
}

/**
 * People's time (T-102): counts this signed-in tab's active minutes and sends them to POST /api/activity
 * about once a minute, and when the tab is hidden or closed. Mounted once, in the signed-in app layout.
 * A browser driven by a script (Playwright, the dev screenshots) never counts.
 */
export function useActivityTracking(): void {
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  useLayoutEffect(() => {
    pathRef.current = pathname;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || navigator.webdriver) return undefined;
    const tracker = new ActivityTracker(
      {
        now: () => Date.now(),
        visible: () => document.visibilityState === 'visible',
        focused: () => document.hasFocus(),
        context: () => currentActivityContext(pathRef.current, openPanelTask(), chatHasTheTab(lastInputInChat)),
      },
      sendActivity
    );
    let lastInput = 0;
    let lastInputInChat = false;
    const onInput = (event: Event) => {
      lastInputInChat = event.target instanceof Element && event.target.closest(CHAT_PANEL) !== null;
      const now = Date.now();
      if (now - lastInput < INPUT_THROTTLE_MS) return;
      lastInput = now;
      tracker.input();
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') void tracker.flush(true);
    };
    const onPageHide = () => void tracker.flush(true);
    const opts: AddEventListenerOptions = { capture: true, passive: true };
    for (const name of INPUT_EVENTS) window.addEventListener(name, onInput, opts);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    const sampler = setInterval(() => tracker.sample(), SAMPLE_MS);
    const flusher = setInterval(() => void tracker.flush(), FLUSH_MS);
    return () => {
      for (const name of INPUT_EVENTS) window.removeEventListener(name, onInput, opts);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
      clearInterval(sampler);
      clearInterval(flusher);
      void tracker.flush(true);
    };
  }, []);
}
